import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import StatsSection from "../components/StatsSection";
import ServicesGrid from "../components/ServicesGrid";
import Gallery from "../components/Gallery";
import ScrollReveal from "../components/ScrollReveal";
import { VALUES } from "../lib/siteData";

export default function Home() {
  return (
    <main data-testid="page-home">
      <HeroSlider />

      {/* About preview - Logo left, narrative right */}
      <section className="bg-[#FCFCFA] py-24 md:py-32" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          <ScrollReveal className="md:col-span-5">
            <div className="relative aspect-square w-full max-w-[340px] sm:max-w-[420px] md:max-w-[520px] mx-auto md:mx-0 flex items-center justify-center">
              <div className="absolute inset-4 border border-[#D4AF37]/40 rounded-full" />
              <div className="absolute inset-10 border border-[#D4AF37]/20 rounded-full" />
              <img
                src="https://customer-assets.emergentagent.com/job_maclear-holdings/artifacts/tofoqejc_1.png"
                alt="Cape Maclear Holdings"
                data-testid="home-who-we-are-logo"
                className="relative w-[88%] h-[88%] object-contain"
              />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15} className="md:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Who We Are</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] text-[#1A1D20] mb-8">
              A master developer,<br />building a nation-scale<br />opportunity.
            </h2>
            <p className="text-[#5C636A] text-[16px] md:text-[18px] leading-relaxed">
              Cape Maclear Holdings Ltd is the master development company
              spearheading the Cape Maclear Special Economic Zone in Mangochi
              District, Malawi. We plan, develop and execute large-scale
              infrastructure — integrating a new <span className="text-[#1A1D20] font-medium">international airport</span>,
              an <span className="text-[#1A1D20] font-medium">International Financial Centre</span>, five-star
              <span className="text-[#1A1D20] font-medium"> hotels and wellness resorts</span>, logistics, agriculture,
              energy and smart-city technology into a single, coherent city
              ecosystem on the shores of Lake Malawi.
            </p>
            <p className="mt-6 text-[#5C636A] text-[16px] leading-relaxed">
              Our work is anchored by a highly skilled multidisciplinary team
              combining local insight with international experience — and is
              shaped by partnerships with global development firms, investors
              and technical experts.
            </p>
            <Link
              to="/about"
              data-testid="about-preview-cta"
              className="mt-10 inline-flex items-center gap-3 text-[#1A1D20] uppercase tracking-[0.2em] text-[12px] font-semibold group"
            >
              Our Story
              <ArrowRight
                size={16}
                strokeWidth={1.5}
                className="group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </ScrollReveal>
        </div>
      </section>

      <StatsSection />

      <ServicesGrid />

      {/* Values strip */}
      <section className="bg-[#FCFCFA] py-24 md:py-28 border-t border-[#E5E5DF]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 mb-14">
            <span className="hairline-gold" />
            <span className="overline">Our Values</span>
          </div>
          <div className="grid md:grid-cols-5 gap-10 md:gap-8">
            {VALUES.map((v, i) => (
              <ScrollReveal key={v.title} delay={i * 0.08}>
                <div className="pr-4">
                  <p className="font-serif-display text-2xl md:text-3xl text-[#1A1D20] mb-4">
                    <span className="text-[#D4AF37]">0{i + 1}</span>&nbsp;&nbsp;{v.title}
                  </p>
                  <p className="text-[14px] leading-relaxed text-[#5C636A]">{v.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <Gallery />

      {/* CTA band */}
      <section className="bg-[#1A1D20] text-[#FCFCFA]" data-testid="cta-band">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Join Us</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">
              Be part of the most transformative project on the African continent.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-white/75 text-[15px] md:text-[17px] leading-relaxed mb-10">
              We invite investors, developers and strategic partners to help
              shape the future of Cape Maclear — and the next chapter of
              Malawi.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/investor-relations"
                data-testid="home-cta-invest"
                className="inline-flex justify-center items-center bg-[#D4AF37] text-[#1A1D20] hover:bg-white transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
              >
                Investor Relations
              </Link>
              <Link
                to="/contact"
                data-testid="home-cta-contact"
                className="inline-flex justify-center items-center border border-white/60 text-white hover:bg-white hover:text-[#1A1D20] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
